import jsPDF from 'jspdf';

/* ─── Colour Palette ─────────────────────────────── */
const C = {
    dark:      '#0D0D0D',
    orange:    '#FF9F1C',
    white:     '#FFFFFF',
    midGray:   '#94A3B8',
    textDark:  '#1E293B',
    textMid:   '#475569',
    textLight: '#CBD5E1',
    accent2:   '#3B82F6',
    green:     '#10B981',
    purple:    '#8B5CF6',
    pageBg:    '#F8FAFC',
};

/* ─── Helpers ────────────────────────────────────── */
const hexToRgb = (hex) => {
    let h = String(hex || '#000000').trim();
    // expand shorthand #RGB → #RRGGBB
    if (/^#[0-9a-fA-F]{3}$/.test(h)) {
        h = '#' + h[1]+h[1] + h[2]+h[2] + h[3]+h[3];
    }
    const r = parseInt(h.slice(1, 3), 16);
    const g = parseInt(h.slice(3, 5), 16);
    const b = parseInt(h.slice(5, 7), 16);
    return [isNaN(r)?0:r, isNaN(g)?0:g, isNaN(b)?0:b];
};

const setFill   = (doc, hex) => { const [r,g,b] = hexToRgb(hex); doc.setFillColor(r,g,b); };
const setStroke = (doc, hex) => { const [r,g,b] = hexToRgb(hex); doc.setDrawColor(r,g,b); };
const setTxt    = (doc, hex) => { const [r,g,b] = hexToRgb(hex); doc.setTextColor(r,g,b); };

/** Keep only Latin-1 so built-in Helvetica never sees NaN text-widths */
// eslint-disable-next-line no-control-regex
const safe = (str) => String(str || '').replace(/[^\x00-\xFF]/g, '');

/** Print wrapped text; returns y after last line */
const wrapText = (doc, text, x, y, maxW, lh = 6) => {
    const lines = doc.splitTextToSize(safe(text), maxW);
    lines.forEach(line => { doc.text(line, x, y); y += lh; });
    return y;
};

/** Horizontal rule */
const hr = (doc, y, color = C.orange, x1 = 20, x2 = 190, lw = 0.4) => {
    setStroke(doc, color);
    doc.setLineWidth(lw);
    doc.line(x1, y, x2, y);
};

/** Orange eyebrow label with letter-spacing */
const eyebrow = (doc, text, x, y) => {
    setTxt(doc, C.orange);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setCharSpace(2.5);
    doc.text(safe(text), x, y);
    doc.setCharSpace(0);
};

/** Shared page footer */
const pageFooter = (doc, pageNum, theme = 'light') => {
    const W = 210, H = 297;
    const bg  = theme === 'dark' ? '#111111' : '#E2E8F0';
    const txt = theme === 'dark' ? C.midGray  : C.textMid;

    setFill(doc, bg);
    doc.rect(0, H - 12, W, 12, 'F');
    hr(doc, H - 12, theme === 'dark' ? '#2A2A2A' : '#CBD5E1', 0, W, 0.25);

    setTxt(doc, txt);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text('Nimai Solutions  |  nimaisolutions.in', 20, H - 4.5);
    setTxt(doc, C.orange);
    doc.setFont('helvetica', 'bold');
    doc.text('Page ' + pageNum + ' / 5', W - 20, H - 4.5, { align: 'right' });
};

/** Load a URL → base-64 PNG data-URL (browser only) */
const loadImageAsDataUrl = (url) => new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
        try {
            const c = document.createElement('canvas');
            c.width = img.naturalWidth; c.height = img.naturalHeight;
            c.getContext('2d').drawImage(img, 0, 0);
            resolve(c.toDataURL('image/png'));
        } catch (_) { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = url;
});

/* ═══════════════════════════════════════════════════
   PAGE 1  ·  Cover
═══════════════════════════════════════════════════ */
const drawCover = (doc, config, logo) => {
    const W = 210, H = 297;

    // Background
    setFill(doc, '#0D0D0D');
    doc.rect(0, 0, W, H, 'F');

    // Top stripe
    setFill(doc, C.orange);
    doc.rect(0, 0, W, 8, 'F');

    // Decorative dark circles
    setFill(doc, '#181818');
    doc.circle(W + 10, 80, 90, 'F');
    setFill(doc, '#141414');
    doc.circle(-10, H - 30, 110, 'F');

    // Subtle orange glow
    setFill(doc, '#1F1200');
    doc.circle(W - 20, 90, 60, 'F');

    // Logo + brand
    if (logo) { try { doc.addImage(logo, 'PNG', 20, 22, 20, 20); } catch(_) {} }
    setTxt(doc, C.white);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('NIMAI SOLUTIONS', 44, 30);
    setTxt(doc, C.midGray);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('nimaisolutions.in', 44, 37);

    setFill(doc, '#222222');
    doc.rect(0, 48, W, 0.5, 'F');

    // Main headline block
    setTxt(doc, '#CCCCCC');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('COMPANY PROFILE', 20, 80);

    setTxt(doc, C.white);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(38);
    doc.text('Nimai', 20, 104);
    setTxt(doc, C.orange);
    doc.text('Solutions', 20, 122);

    hr(doc, 130, C.orange, 20, 90, 1.5);

    setTxt(doc, '#BBBBBB');
    doc.setFontSize(10.5);
    doc.setFont('helvetica', 'normal');
    wrapText(
        doc,
        config.tagline || 'Premium Web & Mobile App Development Studio — Vadodara, India',
        20, 140, 130, 7
    );

    // Orange info bar
    setFill(doc, C.orange);
    doc.roundedRect(20, 162, 140, 22, 3, 3, 'F');
    setTxt(doc, '#0D0D0D');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.text('BUILDING DIGITAL EXCELLENCE', 90, 171, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('Scalable  |  Reliable  |  Future-Ready', 90, 179, { align: 'center' });

    // Service tag pills
    const pills = ['Web Dev', 'Mobile Apps', 'UI / UX', 'Cloud'];
    pills.forEach((p, i) => {
        const px = 20 + i * 36;
        setFill(doc, '#1E1E1E');
        doc.roundedRect(px, 194, 32, 9, 2, 2, 'F');
        setTxt(doc, C.orange);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.text(p, px + 16, 200, { align: 'center' });
    });

    // Bottom contact bar
    setFill(doc, '#111111');
    doc.rect(0, H - 26, W, 26, 'F');
    hr(doc, H - 26, '#2A2A2A', 0, W, 0.3);
    setTxt(doc, C.midGray);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(safe('Email: ' + (config.email || 'hello@nimaisolutions.in')), 20, H - 15);
    doc.text(safe('Phone: ' + (config.phone || '+91 99999 00000')),          20, H - 8);
    setTxt(doc, C.orange);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('nimaisolutions.in', W - 20, H - 10, { align: 'right' });
};

/* ═══════════════════════════════════════════════════
   PAGE 2  ·  About Us
═══════════════════════════════════════════════════ */
const drawAbout = (doc, config) => {
    const W = 210;

    setFill(doc, C.white);
    doc.rect(0, 0, W, 297, 'F');

    // Left accent bar
    setFill(doc, C.orange);
    doc.rect(0, 0, 5, 297, 'F');

    eyebrow(doc, 'WHO WE ARE', 18, 28);

    setTxt(doc, C.textDark);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(26);
    doc.text('About', 18, 42);
    setTxt(doc, C.orange);
    doc.text('Nimai Solutions', 18, 55);

    hr(doc, 61, C.orange, 18, 95, 1.5);

    // Body paragraph
    setTxt(doc, C.textMid);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    let y = wrapText(
        doc,
        config.aboutText ||
        'Nimai Solutions is a boutique digital agency based in Vadodara, Gujarat. We craft high-performance web applications, cross-platform mobile apps, and purposeful UI/UX designs that help businesses grow in the digital era. Our team blends technical mastery with a human-centric design philosophy to deliver products that are not just functional, but truly impactful.',
        18, 72, 174, 6.5
    );

    y += 10;

    // ── Mission / Vision / Values ──
    const boxes = [
        { label: 'MISSION', title: 'Our Mission',  accentColor: C.orange,
          text: config.mission || 'Empower businesses with scalable, elegant digital products that create lasting impact.' },
        { label: 'VISION',  title: 'Our Vision',   accentColor: C.accent2,
          text: config.vision  || 'To be the most trusted and innovative software development studio in India by 2030.' },
        { label: 'VALUES',  title: 'Core Values',  accentColor: C.green,
          text: config.values  || 'Integrity  |  Transparency  |  Scalable Engineering  |  Human-Centric Design' },
    ];

    boxes.forEach((box) => {
        const bH = 30;
        // White card with left colour border
        setFill(doc, C.pageBg);
        setStroke(doc, '#E2E8F0');
        doc.setLineWidth(0.3);
        doc.roundedRect(18, y, 174, bH, 2, 2, 'FD');

        // Left accent strip
        setFill(doc, box.accentColor);
        doc.roundedRect(18, y, 3, bH, 2, 2, 'F');
        doc.rect(20, y, 1, bH, 'F');           // fill the right half of rounded left edge

        // Title
        setTxt(doc, C.textDark);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.text(safe(box.title), 26, y + 11);

        // Body
        setTxt(doc, C.textMid);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        wrapText(doc, box.text, 26, y + 19, 160, 5.5);

        y += bH + 6;
    });

    // ── Why Choose Us highlights ──
    y += 4;
    eyebrow(doc, 'WHY CLIENTS CHOOSE US', 18, y);
    y += 10;

    const highlights = [
        '10+ Years of Experience', '200+ Projects Delivered',
        '50+ Global Clients',      '24/7 Technical Support',
    ];
    highlights.forEach((h, i) => {
        const cx = i % 2 === 0 ? 18 : 110;
        const cy = y + Math.floor(i / 2) * 15;
        setFill(doc, C.pageBg);
        setStroke(doc, '#E2E8F0');
        doc.setLineWidth(0.25);
        doc.roundedRect(cx, cy, 85, 11, 2, 2, 'FD');

        setFill(doc, C.orange);
        doc.circle(cx + 9, cy + 5.5, 3, 'F');
        setTxt(doc, C.white);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6.5);
        doc.text('OK', cx + 9, cy + 7.2, { align: 'center' });

        setTxt(doc, C.textDark);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.text(safe(h), cx + 16, cy + 7.2);
    });

    pageFooter(doc, 2);
};

/* ═══════════════════════════════════════════════════
   PAGE 3  ·  Services
═══════════════════════════════════════════════════ */
const drawServices = (doc, services) => {
    const W = 210;

    setFill(doc, '#0C0C0C');
    doc.rect(0, 0, W, 297, 'F');

    // Top accent strip
    setFill(doc, C.orange);
    doc.rect(0, 0, W, 5, 'F');

    eyebrow(doc, 'WHAT WE OFFER', 18, 24);
    setTxt(doc, C.white);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(26);
    doc.text('Our Services', 18, 38);
    setTxt(doc, C.midGray);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Complete digital solutions tailored to your business goals.', 18, 48);

    hr(doc, 54, '#333333', 18, 192, 0.4);

    // 2-column grid
    const colW = 85, rowH = 48, gap = 7;
    const colX = [18, 107];

    services.slice(0, 6).forEach((svc, idx) => {
        const cx = colX[idx % 2];
        const cy = 60 + Math.floor(idx / 2) * (rowH + gap);

        // Card
        setFill(doc, '#181818');
        setStroke(doc, '#2E2E2E');
        doc.setLineWidth(0.3);
        doc.roundedRect(cx, cy, colW, rowH, 3, 3, 'FD');

        // Orange top accent line on card
        setFill(doc, C.orange);
        doc.roundedRect(cx, cy, colW, 2.5, 3, 3, 'F');
        doc.rect(cx, cy + 1, colW, 1.5, 'F');

        // Number
        setTxt(doc, '#333333');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.text('0' + (idx + 1), cx + colW - 6, cy + 14, { align: 'right' });

        // Title
        setTxt(doc, C.white);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.text(safe(svc.title || 'Service'), cx + 7, cy + 16);

        // Divider
        hr(doc, cy + 20, '#2E2E2E', cx + 7, cx + colW - 7, 0.3);

        // Description
        setTxt(doc, '#999999');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        wrapText(doc, svc.description || '', cx + 7, cy + 27, colW - 14, 5);
    });

    pageFooter(doc, 3, 'dark');
};

/* ═══════════════════════════════════════════════════
   PAGE 4  ·  Stats & Process
═══════════════════════════════════════════════════ */
const drawStats = (doc, stats, _config) => {
    const W = 210;

    setFill(doc, C.white);
    doc.rect(0, 0, W, 297, 'F');

    // Right orange accent bar
    setFill(doc, C.orange);
    doc.rect(W - 5, 0, 5, 297, 'F');

    eyebrow(doc, 'NUMBERS THAT SPEAK', 18, 26);
    setTxt(doc, C.textDark);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(24);
    doc.text('Our Track Record', 18, 38);
    hr(doc, 43, C.orange, 18, 115, 1.5);

    // ── Stat cards — solid color, white text ──
    const cardColors = [C.orange, C.accent2, C.green, C.purple];
    const cardW = 85, cardH = 40;
    const cardXs = [18, 107, 18, 107];
    const cardYs = [50, 50, 96, 96];

    stats.slice(0, 4).forEach((stat, i) => {
        const cx = cardXs[i], cy = cardYs[i];
        const color = cardColors[i];

        // Solid card background
        setFill(doc, color);
        doc.roundedRect(cx, cy, cardW, cardH, 4, 4, 'F');

        // Dark overlay strip at bottom for label
        setFill(doc, '#00000033');           // pure dark at ~20 % — use manual workaround
        // jsPDF doesn't support RGBA natively; approximate with a slightly darker shade of the card colour
        const [cr, cg, cb] = hexToRgb(color);
        doc.setFillColor(
            Math.max(0, cr - 40),
            Math.max(0, cg - 40),
            Math.max(0, cb - 40)
        );
        doc.roundedRect(cx, cy + cardH - 14, cardW, 14, 4, 4, 'F');
        // patch: cover the top corners of the darker strip with the main color
        setFill(doc, color);
        doc.rect(cx, cy + cardH - 14, cardW, 4, 'F');

        // Value — large white
        setTxt(doc, C.white);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(26);
        doc.text(safe(String(stat.value || '--')), cx + 10, cy + 22);

        // Label — small white
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text(safe(stat.label || ''), cx + 10, cy + cardH - 5);
    });

    // ── Process section ──
    let y = 148;
    eyebrow(doc, 'HOW WE WORK', 18, y);
    y += 10;
    setTxt(doc, C.textDark);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text('Our Process', 18, y);
    y += 7;
    hr(doc, y, C.orange, 18, 110, 1.2);
    y += 10;

    const steps = [
        { n: '1', title: 'Discovery',          desc: 'Requirements gathering, scope definition, and technical planning.' },
        { n: '2', title: 'Design & Prototype',  desc: 'Wireframes, UI prototypes, and design system creation.' },
        { n: '3', title: 'Development',         desc: 'Agile sprints with regular demos and client feedback.' },
        { n: '4', title: 'Testing & QA',        desc: 'Cross-device, browser and load testing for quality assurance.' },
        { n: '5', title: 'Launch & Support',    desc: 'Deployment, monitoring and ongoing post-launch support.' },
    ];

    steps.forEach((step) => {
        // Circle
        setFill(doc, C.orange);
        doc.circle(25, y + 4, 5, 'F');
        setTxt(doc, '#0D0D0D');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.5);
        doc.text(step.n, 25, y + 6.5, { align: 'center' });

        // Step connector line (except last)
        if (step.n !== '5') {
            setStroke(doc, '#E2E8F0');
            doc.setLineWidth(0.4);
            doc.line(25, y + 9, 25, y + 20);
        }

        // Text
        setTxt(doc, C.textDark);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.text(safe(step.title), 35, y + 5);

        setTxt(doc, C.textMid);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        wrapText(doc, step.desc, 35, y + 11, 150, 5);

        y += 21;
    });

    pageFooter(doc, 4);
};

/* ═══════════════════════════════════════════════════
   PAGE 5  ·  Contact
═══════════════════════════════════════════════════ */
const drawContact = (doc, config, logo) => {
    const W = 210, H = 297;

    setFill(doc, '#0D0D0D');
    doc.rect(0, 0, W, H, 'F');

    // Orange header band
    setFill(doc, C.orange);
    doc.rect(0, 0, W, 72, 'F');

    // Decorative circle on banner
    setFill(doc, '#E8860A');
    doc.circle(W + 10, 20, 65, 'F');

    // Logo + name in header
    if (logo) { try { doc.addImage(logo, 'PNG', 18, 18, 18, 18); } catch (_) {} }
    setTxt(doc, '#0D0D0D');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text('NIMAI SOLUTIONS', 42, 28);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.text(safe(config.tagline || 'Premium Web & Mobile Development Agency'), 42, 37);

    // "Since / Year" chip
    setFill(doc, '#0D0D0D');
    doc.roundedRect(42, 43, 36, 9, 2, 2, 'F');
    setTxt(doc, C.orange);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.text('EST. 2014', 60, 49, { align: 'center' });

    // Contact heading
    eyebrow(doc, 'REACH US', 18, 86);
    setTxt(doc, C.white);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(24);
    doc.text('Get In Touch', 18, 98);
    hr(doc, 103, C.orange, 18, 95, 1.5);

    // Contact info rows
    const rows = [
        { key: 'Phone',   val: config.phone   || '+91 99999 00000'           },
        { key: 'Email',   val: config.email   || 'hello@nimaisolutions.in'   },
        { key: 'Website', val: config.website || 'https://nimaisolutions.in' },
        { key: 'Address', val: config.address || 'Vadodara, Gujarat, India'   },
    ];

    let y = 110;
    rows.forEach((row) => {
        // Row bg
        setFill(doc, '#181818');
        setStroke(doc, '#2A2A2A');
        doc.setLineWidth(0.3);
        doc.roundedRect(18, y, 174, 18, 2, 2, 'FD');

        // Key pill
        setFill(doc, C.orange);
        doc.roundedRect(23, y + 4, 26, 9, 2, 2, 'F');
        setTxt(doc, '#0D0D0D');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.text(row.key.toUpperCase(), 36, y + 10, { align: 'center' });

        // Value
        setTxt(doc, C.white);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text(safe(row.val), 54, y + 11);

        y += 24;
    });

    // CTA banner
    y += 6;
    setFill(doc, C.orange);
    doc.roundedRect(18, y, 174, 32, 4, 4, 'F');
    setTxt(doc, '#0D0D0D');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Ready to Build Something Amazing?', W / 2, y + 15, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Contact us today for a FREE consultation.', W / 2, y + 24, { align: 'center' });

    // Footer
    setFill(doc, '#111111');
    doc.rect(0, H - 16, W, 16, 'F');
    hr(doc, H - 16, '#2A2A2A', 0, W, 0.3);
    setTxt(doc, C.midGray);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.text('(c) ' + new Date().getFullYear() + ' Nimai Solutions. All rights reserved.', 18, H - 6);
    setTxt(doc, C.orange);
    doc.setFont('helvetica', 'bold');
    doc.text('nimaisolutions.in', W - 18, H - 6, { align: 'right' });
};

/* ═══════════════════════════════════════════════════
   MASTER EXPORT
═══════════════════════════════════════════════════ */
export const generateBrochurePDF = async (config = {}, services = [], stats = []) => {
    const logoUrl     = config.logoUrl || (window.location.origin + '/logo.png');
    const logoDataUrl = await loadImageAsDataUrl(logoUrl);

    const finalServices = services.length > 0 ? services : [
        { title: 'Web Development',   description: 'High-performance React and Node.js applications built for scale.' },
        { title: 'Mobile Apps',       description: 'Flutter and React Native apps for iOS and Android.' },
        { title: 'UI/UX Design',      description: 'Human-centric, accessible interfaces that convert.' },
        { title: 'Cloud Platforms',   description: 'AWS, Azure, and GCP infrastructure optimisation.' },
        { title: 'E-Commerce',        description: 'Custom stores with seamless payment integration.' },
        { title: 'Digital Marketing', description: 'SEO, SEM, and content strategies that grow reach.' },
    ];

    const finalStats = stats.length > 0 ? stats : [
        { label: 'Years Experience',   value: '10+',  subtitle: 'Est. 2014'      },
        { label: 'Projects Delivered', value: '200+', subtitle: '+12 this month' },
        { label: 'Global Clients',     value: '50+',  subtitle: '+5 countries'   },
        { label: 'Tech Support',       value: '24/7', subtitle: 'Always on'      },
    ];

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    drawCover(doc, config, logoDataUrl);
    doc.addPage(); drawAbout(doc, config);
    doc.addPage(); drawServices(doc, finalServices);
    doc.addPage(); drawStats(doc, finalStats, config);
    doc.addPage(); drawContact(doc, config, logoDataUrl);

    const fname = 'Nimai_Solutions_Brochure_' + new Date().getFullYear() + '.pdf';
    doc.save(fname);
    return fname;
};

export default generateBrochurePDF;
